import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, MessageSquare, Voicemail, Clock, User, Search, Archive, Star, AlertCircle, CheckCircle, ArrowRight, Bot } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallCenterInbox() {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('unread');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchInteractions = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/call-center/interactions?page_size=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInteractions(response.data.interactions || []);
    } catch (err) {
      console.error('Failed to fetch interactions:', err);
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInteractions();
  }, [fetchInteractions]);

  // Filter interactions based on tab and search
  const filterInteractions = () => {
    let filtered = [...interactions];
    
    // Filter by search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(i => 
        i.caller_name?.toLowerCase().includes(search) ||
        i.caller_number?.includes(search) ||
        i.contact_name?.toLowerCase().includes(search)
      );
    }
    
    // Filter by tab
    switch (activeTab) {
      case 'unread':
        filtered = filtered.filter(i => i.status === 'pending' || i.status === 'missed');
        break;
      case 'missed':
        filtered = filtered.filter(i => i.status === 'missed' || i.interaction_type === 'missed_call');
        break;
      case 'voicemail':
        filtered = filtered.filter(i => i.interaction_type === 'voicemail' || i.status === 'voicemail');
        break;
      case 'ai_handled':
        filtered = filtered.filter(i => i.handled_by_ai);
        break;
      case 'callbacks':
        filtered = filtered.filter(i => i.requires_callback);
        break;
      default:
        break;
    }
    
    return filtered;
  };

  const getIcon = (type) => {
    switch (type) {
      case 'inbound_call': return PhoneIncoming;
      case 'outbound_call': return PhoneOutgoing;
      case 'missed_call': return PhoneMissed;
      case 'voicemail': return Voicemail;
      case 'sms_inbound':
      case 'sms_outbound': return MessageSquare;
      default: return Phone;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'normal': return 'bg-blue-500';
      case 'low': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const filteredItems = filterInteractions();

  const counts = {
    all: interactions.length,
    unread: interactions.filter(i => i.status === 'pending' || i.status === 'missed').length,
    missed: interactions.filter(i => i.status === 'missed' || i.interaction_type === 'missed_call').length,
    voicemail: interactions.filter(i => i.interaction_type === 'voicemail' || i.status === 'voicemail').length,
    ai_handled: interactions.filter(i => i.handled_by_ai).length,
    callbacks: interactions.filter(i => i.requires_callback).length
  };

  return (
    <Layout>
    <div className="p-6 h-[calc(100vh-64px)] flex flex-col">
      <div className="mb-4">
        <h1 className="text-2xl font-bold">Call Center Inbox</h1>
        <p className="text-muted-foreground">Manage incoming interactions and follow-ups</p>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left Panel - List */}
        <Card className="flex-1 flex flex-col min-w-0">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search interactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
                <TabsTrigger value="unread">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Unread ({counts.unread})
                </TabsTrigger>
                <TabsTrigger value="missed">
                  <PhoneMissed className="h-3 w-3 mr-1" />
                  Missed ({counts.missed})
                </TabsTrigger>
                <TabsTrigger value="voicemail">
                  <Voicemail className="h-3 w-3 mr-1" />
                  Voicemail ({counts.voicemail})
                </TabsTrigger>
                <TabsTrigger value="ai_handled">
                  <Bot className="h-3 w-3 mr-1" />
                  AI ({counts.ai_handled})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden pt-0">
            <ScrollArea className="h-full pr-4">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No items in this category</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredItems.map((item) => {
                    const Icon = getIcon(item.interaction_type);
                    const isSelected = selectedItem?.id === item.id;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedItem(item)}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
                          isSelected
                            ? 'bg-primary/10 border-primary'
                            : 'hover:bg-accent border-transparent'
                        }`}
                      >
                        <div className={`w-1 h-10 rounded-full ${getPriorityColor(item.priority)}`} />
                        <div className={`p-2 rounded-full ${
                          item.status === 'missed' ? 'bg-red-100' :
                          item.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          <Icon className={`h-4 w-4 ${
                            item.status === 'missed' ? 'text-red-600' :
                            item.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">
                              {item.caller_name || item.contact_name || 'Unknown'}
                            </span>
                            {item.handled_by_ai && (
                              <Bot className="h-3 w-3 text-purple-500" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {item.caller_number}
                          </p>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          {formatTime(item.created_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Panel - Detail */}
        <Card className="w-[400px] flex-shrink-0 flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg">Details</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto">
            {selectedItem ? (
              <div className="space-y-4">
                <div className="text-center pb-4 border-b">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                    <User className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">
                    {selectedItem.caller_name || selectedItem.contact_name || 'Unknown'}
                  </h3>
                  <p className="text-muted-foreground">{selectedItem.caller_number}</p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <span className="capitalize">{selectedItem.interaction_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge variant={selectedItem.status === 'completed' ? 'default' : 'secondary'}>
                      {selectedItem.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span>{selectedItem.duration_seconds ? `${Math.floor(selectedItem.duration_seconds / 60)}:${(selectedItem.duration_seconds % 60).toString().padStart(2, '0')}` : '-'}</span>
                  </div>
                  {selectedItem.queue_name && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Queue</span>
                      <span>{selectedItem.queue_name}</span>
                    </div>
                  )}
                  {selectedItem.disposition && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Disposition</span>
                      <span className="capitalize">{selectedItem.disposition.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {selectedItem.source && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Source</span>
                      <span>{selectedItem.source}</span>
                    </div>
                  )}
                </div>

                {selectedItem.recording_url && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">Recording</p>
                    <audio controls className="w-full">
                      <source src={selectedItem.recording_url} />
                    </audio>
                  </div>
                )}

                <div className="pt-4 space-y-2">
                  <Button className="w-full" variant="default">
                    <Phone className="h-4 w-4 mr-2" />
                    Call Back
                  </Button>
                  <Button className="w-full" variant="outline">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Send SMS
                  </Button>
                  {selectedItem.contact_id && (
                    <Button className="w-full" variant="outline">
                      <User className="h-4 w-4 mr-2" />
                      View Contact
                      <ArrowRight className="h-4 w-4 ml-auto" />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Phone className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Select an interaction to view details</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </Layout>
  );
}
